import { Empty, Spin } from 'antd';
import { Column, DualAxes, Pie } from '@ant-design/plots';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

const ChartShell = ({ title, loading, empty, emptyDescription, children }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-4 min-h-[280px] flex items-center justify-center">
        <Spin />
      </div>
    );
  }

  if (empty) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        {title && <div className="font-semibold mb-3">{title}</div>}
        <Empty description={emptyDescription} />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      {title && <div className="font-semibold mb-3">{title}</div>}
      {children}
    </div>
  );
};

ChartShell.propTypes = {
  title: PropTypes.string,
  loading: PropTypes.bool,
  empty: PropTypes.bool,
  emptyDescription: PropTypes.string,
  children: PropTypes.node,
};

const toChartRows = (rows = []) =>
  rows
    .map((row) => ({
      type: String(row.key || row.type || '—'),
      value: Number(row.count ?? row.value ?? 0),
    }))
    .filter((row) => row.value > 0);

export default function ChargePointCharts({
  series = [],
  operatorBreakdown = [],
  byStatus = [],
  byPowerType = [],
  byConnectorType = [],
  totalChargersAdded = 0,
  loading = false,
  height = 280,
}) {
  const { t } = useTranslation('reportPage');

  const statusData = toChartRows(byStatus);
  const powerData = toChartRows(byPowerType);
  const connectorData = toChartRows(byConnectorType);
  const operatorData = (operatorBreakdown || [])
    .map((row) => ({
      type: row.operatorName || String(row.operatorId || '—'),
      value: Number(row.chargerCount || 0),
    }))
    .filter((row) => row.value > 0);

  const growthData = (series || []).map((row) => ({
    date: row.date,
    totalChargers: Number(row.totalChargers || 0),
    added: Number(row.added || 0),
  }));
  const showGrowth = Number(totalChargersAdded) > 0 && growthData.length > 0;

  return (
    <div className="space-y-4 mt-4">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <ChartShell
          title={t('chargersByStatus')}
          loading={loading}
          empty={!statusData.length}
          emptyDescription={t('noDataAvailable')}
        >
          <Pie
            data={statusData}
            angleField="value"
            colorField="type"
            height={height}
            autoFit
            legend={{ position: 'bottom' }}
            innerRadius={0.55}
            label={{ text: 'value', style: { fontSize: 12 } }}
          />
        </ChartShell>

        <ChartShell
          title={t('chargersByPowerType')}
          loading={loading}
          empty={!powerData.length}
          emptyDescription={t('noDataAvailable')}
        >
          <Column
            data={powerData}
            xField="type"
            yField="value"
            height={height}
            autoFit
            legend={false}
            style={{ maxWidth: 64, fill: '#067C60' }}
            axis={{
              y: {
                label: {
                  formatter: (val) => String(Math.round(Number(val))),
                },
              },
            }}
          />
        </ChartShell>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <ChartShell
          title={t('chargersByConnectorType')}
          loading={loading}
          empty={!connectorData.length}
          emptyDescription={t('noDataAvailable')}
        >
          <Column
            data={connectorData}
            xField="type"
            yField="value"
            height={height}
            autoFit
            legend={false}
            style={{ maxWidth: 48, fill: '#069C7A' }}
            axis={{
              x: {
                label: {
                  autoRotate: true,
                  style: { fontSize: 10 },
                },
              },
              y: {
                label: {
                  formatter: (val) => String(Math.round(Number(val))),
                },
              },
            }}
          />
        </ChartShell>

        {(loading || operatorData.length > 0) && (
          <ChartShell
            title={t('chargersByOperator')}
            loading={loading}
            empty={!operatorData.length}
            emptyDescription={t('noDataAvailable')}
          >
            <Pie
              data={operatorData}
              angleField="value"
              colorField="type"
              height={height}
              autoFit
              legend={{ position: 'bottom' }}
              innerRadius={0.55}
              label={{ text: 'value', style: { fontSize: 12 } }}
            />
          </ChartShell>
        )}
      </div>

      {showGrowth && (
        <ChartShell
          title={t('chargePointGrowth')}
          loading={loading}
          empty={false}
          emptyDescription={t('noDataAvailable')}
        >
          <DualAxes
            data={growthData}
            xField="date"
            height={height}
            autoFit
            legend={{ position: 'top-left' }}
            children={[
              {
                type: 'interval',
                yField: 'added',
                style: { maxWidth: 24, fill: '#069C7A' },
                axis: {
                  y: {
                    title: t('added'),
                    grid: null,
                    label: {
                      formatter: (val) => String(Math.round(Number(val))),
                    },
                  },
                },
              },
              {
                type: 'line',
                yField: 'totalChargers',
                style: { stroke: '#067C60', lineWidth: 2 },
                axis: {
                  y: {
                    position: 'right',
                    title: t('totalChargers'),
                    label: {
                      formatter: (val) => String(Math.round(Number(val))),
                    },
                  },
                },
              },
            ]}
            axis={{
              x: {
                label: {
                  autoRotate: true,
                  style: { fontSize: 10 },
                },
              },
            }}
          />
        </ChartShell>
      )}
    </div>
  );
}

ChargePointCharts.propTypes = {
  series: PropTypes.arrayOf(PropTypes.object),
  operatorBreakdown: PropTypes.arrayOf(PropTypes.object),
  byStatus: PropTypes.arrayOf(PropTypes.object),
  byPowerType: PropTypes.arrayOf(PropTypes.object),
  byConnectorType: PropTypes.arrayOf(PropTypes.object),
  totalChargersAdded: PropTypes.number,
  loading: PropTypes.bool,
  height: PropTypes.number,
};
